import React, { useState, useRef, useEffect } from 'react';
import { X, CheckCircle, Loader2, Download, Package } from 'lucide-react';
import api from '../utils/api';
import { useFocusTrap } from '../hooks/useFocusTrap';

/**
 * Bundle Modal - Complete Bundle Purchase Flow
 * 
 * Features:
 * - Bundle preview with pricing breakdown
 * - Stripe payment integration ($200)
 * - PDF generation and download
 * - Success confirmation
 */

interface BundleModalProps {
  isOpen: boolean;
  onClose: () => void;
  claimId: string;
  claimData?: {
    client_name: string;
    total_claim: number;
    event_type?: string;
  };
  demoMode?: boolean;
}

export default function BundleModal({ 
  isOpen, 
  onClose, 
  claimId, 
  claimData,
  demoMode = false 
}: BundleModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, isOpen);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const [step, setStep] = useState<'preview' | 'payment' | 'generating' | 'success'>('preview');
  const [bundlePath, setBundlePath] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handlePurchase = async () => {
    if (demoMode) {
      // Demo mode: Mock payment and show success
      setStep('generating');
      
      // Simulate bundle generation
      setTimeout(() => {
        setBundlePath('/api/v1/download/sample-pdf'); // Use existing sample PDF
        setStep('success');
      }, 2000);
      
      return;
    }

    // Real mode: Stripe checkout
    setStep('payment');
    
    try {
      // TODO: Implement Stripe checkout
      // For now, proceed to generation
      setStep('generating');
      
      // Call bundle creation API
      const result = await api.post('/api/v1/bundle/create', {
        claim_id: claimId,
        include_cover: true,
        include_toc: true
      });
      
      if (result.success) {
        setBundlePath(result.bundle.bundle_path);
        setStep('success');
      } else {
        throw new Error(result.error || 'Bundle creation failed');
      }
      
    } catch (err: any) {
      setError(err.message);
      setStep('preview');
    }
  };

  const handleDownload = () => {
    if (bundlePath) {
      window.open(bundlePath, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Complete Bundle" className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-600 text-white p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Package className="w-7 h-7" />
                Complete Bundle - $200
              </h2>
              <p className="text-blue-100 mt-1">25-page comprehensive report package</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          
          {/* Preview Step */}
          {step === 'preview' && (
            <div className="space-y-6">
              
              {/* Claim Info */}
              {claimData && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-blue-900">Claim Details</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Client</p>
                      <p className="font-semibold">{claimData.client_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Total Claim</p>
                      <p className="font-semibold">${claimData.total_claim?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Bundle Contents */}
              <div>
                <h3 className="font-semibold mb-3 text-lg">📦 What's Included</h3>
                <div className="space-y-3">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">Professional Cover Page</p>
                        <p className="text-xs text-gray-600 mt-1">DisruptionGenius branding with claim details</p>
                      </div>
                      <span className="text-xs text-gray-500">1 page</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">Table of Contents</p>
                        <p className="text-xs text-gray-600 mt-1">Detailed navigation with page numbers</p>
                      </div>
                      <span className="text-xs text-gray-500">1 page</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">Standard Standard BI Report</p>
                        <p className="text-xs text-gray-600 mt-1">Complete calculations, charts, causation tests</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-gray-500">11 pages</span>
                        <p className="text-xs text-green-600 font-semibold">FREE</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">AI Legal Narrative</p>
                        <p className="text-xs text-gray-600 mt-1">Executive summary, causation, attestation</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-gray-500">14 pages</span>
                        <p className="text-xs text-blue-600 font-semibold">$150</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing Breakdown */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-50 rounded-lg p-5 border-2 border-blue-200">
                <h3 className="font-semibold mb-3">💰 Pricing</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Standard Report</span>
                    <span className="font-mono text-green-600">FREE</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">AI Narrative</span>
                    <span className="font-mono">$150.00</span>
                  </div>
                  <div className="flex justify-between text-red-600">
                    <span>Bundle Discount</span>
                    <span className="font-mono">-$50.00</span>
                  </div>
                  <div className="border-t-2 border-blue-300 pt-2 flex justify-between font-bold text-lg">
                    <span>Complete Bundle</span>
                    <span className="font-mono text-blue-600">$200.00</span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-3 text-center">
                  ✨ Save $50 compared to purchasing separately
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Demo Notice */}
              {demoMode && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-yellow-800 text-sm">
                  <strong>🎭 Demo Mode:</strong> This is a demonstration. In production, you'll be charged $200 via Stripe.
                </div>
              )}
            </div>
          )}

          {/* Generating Step */}
          {step === 'generating' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin mb-4" />
              <p className="text-lg font-semibold">Generating your bundle...</p>
              <p className="text-sm text-gray-600 mt-2">This may take 10-20 seconds</p>
              <div className="mt-6 space-y-2 text-sm text-gray-600">
                <p>✓ Merging Standard Standard BI Report</p>
                <p>✓ Merging AI Legal Narrative</p>
                <p>✓ Adding cover page and table of contents</p>
                <p>✓ Creating bookmarks...</p>
              </div>
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-green-600 mb-2">Bundle Ready!</h3>
              <p className="text-gray-600 mb-6">
                Your 27-page Complete Bundle has been generated successfully.
              </p>

              {/* Bundle Stats */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6 inline-block">
                <div className="grid grid-cols-3 gap-6 text-sm">
                  <div>
                    <p className="text-gray-600">Pages</p>
                    <p className="font-bold text-xl">27</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Sections</p>
                    <p className="font-bold text-xl">4</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Price</p>
                    <p className="font-bold text-xl text-blue-600">$200</p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleDownload}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg shadow-lg transition flex items-center gap-2 mx-auto"
              >
                <Download className="w-5 h-5" />
                Download Bundle PDF
              </button>

              <p className="text-xs text-gray-500 mt-4">
                📧 A copy has been sent to your email
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'preview' && (
          <div className="border-t border-gray-200 p-6 flex justify-between items-center bg-gray-50 rounded-b-xl">
            <div>
              <p className="text-sm text-gray-600">
                ✓ Instant download after purchase
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={handlePurchase}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-2 rounded-lg transition shadow-lg"
              >
                {demoMode ? 'Generate Bundle (Demo)' : 'Purchase $200'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
